import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import { SKILLS_PATH_NAME } from './skills.constants';
import {
  AiCreateSkillBodySchema,
  AiCreateSkillRejectedResponseSchema,
  CreateSkillBodySchema,
  GetSkillResponseSchema,
  ListSkillAuditResponseSchema,
  ListSkillVersionsResponseSchema,
  ListSkillsQuerySchema,
  ListSkillsResponseSchema,
  PlatformResyncResponseSchema,
  PublishSkillBodySchema,
  RollbackSkillBodySchema,
  SkillApplicationScopeQuerySchema,
  SkillMutationResponseSchema,
  UpdateSkillDraftBodySchema,
} from './skills.dto';

const SkillIdParamsSchema = z.object({ skillId: z.string() });

export const SkillsContract = terraContract
  .prefix(`/${SKILLS_PATH_NAME}`)
  .router({
    listSkills: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List skills for the current tenant (plus platform skills)',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ListSkillsQuerySchema }))
      .output(ListSkillsResponseSchema),
    getSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{skillId}',
        summary: 'Get a single skill with its published and draft versions',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
        }),
      )
      .output(GetSkillResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    listSkillVersions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{skillId}/versions',
        summary: 'List all versions for a skill (newest first)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
        }),
      )
      .output(ListSkillVersionsResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    listSkillAudit: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{skillId}/audit',
        summary: 'List audit entries for a skill (newest first)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
        }),
      )
      .output(ListSkillAuditResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    createSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary:
          'Create a new customer skill (initial draft, optional publish)',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateSkillBodySchema }))
      .output(SkillMutationResponseSchema)
      .errors({
        FORBIDDEN: { status: 403, data: z.object({ message: z.string() }) },
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      }),
    aiCreateSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/ai-create',
        summary: 'Validate, refine, and create a customer skill using AI',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: AiCreateSkillBodySchema }))
      .output(SkillMutationResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: AiCreateSkillRejectedResponseSchema },
        FORBIDDEN: { status: 403, data: AiCreateSkillRejectedResponseSchema },
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      }),
    updateSkillDraft: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{skillId}/draft',
        summary: 'Replace the active draft version with new content',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
          body: UpdateSkillDraftBodySchema,
        }),
      )
      .output(SkillMutationResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: AiCreateSkillRejectedResponseSchema },
        FORBIDDEN: { status: 403, data: AiCreateSkillRejectedResponseSchema },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    publishSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{skillId}/publish',
        summary: 'Publish the active draft (or a specific versionId)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
          body: PublishSkillBodySchema,
        }),
      )
      .output(SkillMutationResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: AiCreateSkillRejectedResponseSchema },
        FORBIDDEN: { status: 403, data: AiCreateSkillRejectedResponseSchema },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    rollbackSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{skillId}/rollback',
        summary: 'Repoint publishedVersionId to an earlier published version',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
          body: RollbackSkillBodySchema,
        }),
      )
      .output(SkillMutationResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    deleteSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{skillId}',
        summary: 'Soft-delete a skill (tombstone)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    restoreSkill: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{skillId}/restore',
        summary: 'Restore a previously tombstoned skill',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SkillIdParamsSchema,
          query: SkillApplicationScopeQuerySchema,
        }),
      )
      .output(SkillMutationResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    triggerPlatformResync: terraContract
      .meta({
        permissions: ['Cockpit Admin'],
      })
      .route({
        method: 'POST',
        path: '/platform/resync',
        summary:
          'Re-run the repo MD -> DB sync for platform skills (operator only)',
        inputStructure: 'detailed',
      })
      .output(PlatformResyncResponseSchema),
  });
